import React, { useRef, useCallback, useState, useMemo, useEffect } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import Fuse from 'fuse.js'
import Color from 'color'
import { useHistory, useLocation } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'

import concepts from 'data/output/concept-nodes.json'
import lmTree from 'data/output/lm-tree.json'

import { ParagraphSmallCaps, Paragraph1 } from 'components/Paragraphs'
import { Label2 } from 'components/Labels'
import { useSearch } from 'flow/interactions/accessors'

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-left: 25px;
`

const Label = styled(Label2)(({ theme }) => css`
  color: ${theme.blueElectric};
  margin-right: 10px;
  opacity: 0.4;
`)

const lmOptions = []
const conceptOptions = concepts
  .filter((d) => d.type !== 'ontology' && d.type !== 'competency')
  .map((d) => ({
    id: d.id,
    label: d.label
  }))

const walk = (node) => {
  if (node.nature !== 'concept') {
    lmOptions.push({
      id: node.id,
      label: node.label,
      nature: node.nature
    })
  }

  node.children.forEach(walk)
}
walk(lmTree)

const Input = styled.input`
  width: 380px;
  height: 40px;
  padding: 15px 10px;
  background: ${({ theme }) => Color(theme.blueDark).alpha(0.1).toString()};
  color: ${({ theme }) => theme.blueElectric};
  border-radius: 2px;
  outline: none;
  border: none;
  &:placeholder {
    color: ${({ theme }) => Color(theme.blueElectric).alpha(0.3).toString()};
  }
`
const Results = styled.div`
  position: absolute;
  right: 0;
  width: 380px;
  border-radius: 5px;
  padding: 0px 20px 25px 0px;
  background: ${({ theme }) => theme.white};
  z-index: 3;
  top: 52px;
  border: 1px solid #E6E6F4;
  box-sizing: border-box;
  border-radius: 5px;
`
const ResultsPart = styled.div`
  padding-top: 29px;
`
const ResultTitle = styled(ParagraphSmallCaps)(({ theme }) => css`
  color: ${Color(theme.blueDark).alpha(0.6).toString()};
  text-transform: uppercase;
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  padding-left: 20px;
`)
const ResultRowContainer = styled(Paragraph1)`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 7px 7px 6px 25px;
  border-left: 5px solid ${({ isHighlighted, theme }) => isHighlighted ? theme.tonic : 'transparent'};
`
const ResultLabel = styled.span`
  padding-right: 7px;
`
const Nature = styled.span`
  color: ${({ theme }) => Color(theme.blueElectric).toString()};
  opacity: 0.4;
  text-transform: capitalize;
`
const Id = styled.span`
  padding-right: 5px;
  padding-left: 7px;
`
const NoResult = styled(Paragraph1)(({ theme }) => css`
  color: ${theme.blueElectric};
  padding: 25px 20px 0 20px;
  opacity: 0.4;
`)

const ResultRow = ({ onMouseEnter, onClick, isHighlighted, id, children }) => {
  const onMouseEnterFn = useCallback(() => {
    onMouseEnter(id)
  }, [id, onMouseEnter])

  const onClickFn = useCallback(() => {
    onClick(id)
  }, [id, onClick])

  return (
    <ResultRowContainer
      isHighlighted={isHighlighted}
      onMouseEnter={onMouseEnterFn}
      onClick={onClickFn}
    >
      {children}
    </ResultRowContainer>
  )
}

const Search = () => {
  const history = useHistory()
  const queryparams = useLocation()
  const inputRef = useRef()
  const [search, setSearch] = useSearch()
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [results, setResults] = useState({
    lm: [],
    concepts: []
  })

  const fuses = useMemo(() => {
    const keys = {
      shouldSort: true,
      threshold: 0.2,
      distance: 5,
      minMatchCharLength: 3,

      keys: ['id', 'label']
    }

    return {
      lm: new Fuse(lmOptions, keys),
      concepts: new Fuse(conceptOptions, keys)
    }
  }, [])

  useEffect(() => {
    const { lm, concepts } = fuses
    if (searchValue) {
      setResults({
        lm: lm.search(searchValue).slice(0, 5).map((d) => d.item),
        concepts: concepts.search(searchValue).slice(0, 5).map((d) => d.item)
      })
    } else {
      setResults({
        lm: [],
        concepts: []
      })
    }
  }, [searchValue, fuses])

  useEffect(() => {
    const close = () => setIsOpen(false)
    if (isOpen) window.addEventListener('click', close, false)
    return () => {
      window.removeEventListener('click', close, false)
    }
  }, [isOpen])

  const onFocus = useCallback(() => {
    setIsOpen(true)
  }, [setIsOpen])

  const stopPropagation = useCallback((e) => {
    e.stopPropagation()
  }, [])

  const onMouseLeave = useCallback(() => {
    setSearch(null)
  }, [setSearch])

  const handleInput = useCallback((e) => {
    setSearchValue(e.target.value)
  }, [setSearchValue])

  const handleESC = useCallback((e) => {
    if (e.keyCode === 27) {
      setSearchValue('')
      setSearch(null)
    }
  }, [setSearchValue, setSearch])

  const onResultClick = useCallback(
    (id) => {
      setSearchValue('')
      setSearch(null)
      history.push(`/${id}${queryparams.search}`)
    },
    [queryparams.search, setSearchValue, history, setSearch]
  )

  return (
    <Container onClick={stopPropagation}>
      <Label>
        <FormattedMessage id='search-title' />
      </Label>
      <Input
        ref={inputRef}
        type='text'
        value={searchValue}
        placeholder={'Eg.: "Maths", "gr13", "Photosynthesis", "KG1002"'}
        onChange={handleInput}
        onFocus={onFocus}
        onKeyDown={handleESC}
      />
      {
        (isOpen && searchValue) && (
          <Results onClick={stopPropagation}>
            {
              (searchValue && !results.lm.length && !results.concepts.length)
                ? (
                  <NoResult>
                    <FormattedMessage id='search-noresult' />
                  </NoResult>
                )
                : (
                  <div>
                    {
                      !!results.lm.length && (
                        <ResultsPart onMouseLeave={onMouseLeave}>
                          <ResultTitle>
                            <FormattedMessage id='search-group-lm' />
                          </ResultTitle>
                          <div onMouseLeave={onMouseLeave}>
                            {
                              results.lm.map((d) => {
                                return (
                                  <ResultRow
                                    key={`lm-${d.id}`}
                                    id={d.id}
                                    isHighlighted={search === d.id}
                                    onMouseEnter={setSearch}
                                    onClick={onResultClick}
                                  >
                                    <span>
                                      <ResultLabel>
                                        {d.label}
                                      </ResultLabel>
                                      <Nature>
                                        {d.nature}
                                      </Nature>
                                    </span>
                                    <Id>
                                      {d.id.toUpperCase()}
                                    </Id>
                                  </ResultRow>
                                )
                              })
                            }
                          </div>
                        </ResultsPart>
                      )
                    }
                    {
                      !!results.concepts.length && (
                        <ResultsPart>
                          <ResultTitle>
                            <FormattedMessage id='search-group-concept' />
                          </ResultTitle>
                          <div onMouseLeave={onMouseLeave}>
                            {
                              results.concepts.map((d) => {
                                return (
                                  <ResultRow
                                    key={`concept-${d.id}`}
                                    id={d.id}
                                    isHighlighted={search === d.id}
                                    onMouseEnter={setSearch}
                                    onClick={onResultClick}
                                  >
                                    <span>
                                      {d.label}
                                    </span>
                                    <Id>
                                      {d.id.toUpperCase()}
                                    </Id>
                                  </ResultRow>
                                )
                              })
                            }
                          </div>
                        </ResultsPart>
                      )
                    }
                  </div>
                )
            }
          </Results>
        )
      }
    </Container>
  )
}

export default Search
